// src/pages/Flashcards.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import { useAuth } from '../context/AuthContext';
import { useAuthModal } from '../context/AuthModalContext';
import { useUserTier } from '../context/UserTierContext';
import Navbar from '../components/Navbar';
import { useTheme, TOKENS } from '../context/ThemeContext';

const API = process.env.REACT_APP_BACKEND_URL;

const QUALITY_AGAIN = 0;
const QUALITY_GOT_IT = 2;
const QUALITY_EASY = 3;

// ── Deck List ────────────────────────────────────────────────────────────────
function DeckList({ decks, onSelect, loading, c }) {
  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
      <div style={{ width: 40, height: 40, border: `3px solid ${c.border}`, borderTop: `3px solid ${c.accent}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    </div>
  );

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, justifyContent: 'center', maxWidth: 960, margin: '0 auto' }}>
      {decks.map(deck => (
        <div key={deck.id}
          onClick={() => onSelect(deck)}
          style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 14, padding: '24px 20px', width: 280, cursor: 'pointer', transition: 'border-color 0.15s, transform 0.15s', display: 'flex', flexDirection: 'column', gap: 8, opacity: !deck.is_free ? 0.9 : 1 }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = c.accent; e.currentTarget.style.transform = 'translateY(-3px)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = c.border; e.currentTarget.style.transform = 'translateY(0)'; }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: c.muted, textTransform: 'uppercase', letterSpacing: 1 }}>{deck.topic}</span>
            {!deck.is_free
              ? <span style={{ fontSize: 10, fontWeight: 700, background: c.accentBg, color: c.accent, padding: '2px 8px', borderRadius: 99, border: `1px solid ${c.accentBorder}` }}>PRO</span>
              : <span style={{ fontSize: 10, fontWeight: 700, background: c.success + '22', color: c.success, padding: '2px 8px', borderRadius: 99 }}>FREE</span>
            }
          </div>
          <div style={{ fontSize: 17, fontWeight: 600, color: c.text }}>{deck.title}</div>
          <div style={{ fontSize: 13, color: c.secondary, lineHeight: 1.5 }}>{deck.description}</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
            <span style={{ fontSize: 12, color: c.muted }}>{deck.card_count} cards</span>
            {deck.due_count > 0 && (
              <span style={{ fontSize: 12, fontWeight: 600, background: '#f59e0b22', color: '#f59e0b', padding: '2px 10px', borderRadius: 99 }}>{deck.due_count} due</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Study Session ────────────────────────────────────────────────────────────
function StudySession({ deck, cards, onFinish, onRate, userId, c }) {
  const [index, setIndex]     = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [results, setResults] = useState([]);
  const [done, setDone]       = useState(false);

  const card = cards[index];

  async function rate(quality) {
    if (userId) await onRate(card.id, quality);
    const label = quality === QUALITY_AGAIN ? 'again' : quality === QUALITY_GOT_IT ? 'gotIt' : 'easy';
    setResults(r => [...r, label]);
    if (index + 1 >= cards.length) {
      setDone(true);
    } else {
      setIndex(i => i + 1);
      setFlipped(false);
    }
  }

  if (done) {
    const again = results.filter(r => r === 'again').length;
    const gotIt = results.filter(r => r === 'gotIt').length;
    const easy  = results.filter(r => r === 'easy').length;
    return (
      <div style={{ maxWidth: 480, margin: '0 auto', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
        <div style={{ fontSize: 56 }}>🎉</div>
        <h2 style={{ fontSize: 28, fontWeight: 700, color: c.text, margin: 0 }}>Session Complete!</h2>
        <p style={{ fontSize: 15, color: c.secondary, margin: 0 }}>{deck.title}</p>
        <div style={{ display: 'flex', gap: 16, margin: '8px 0' }}>
          <div style={{ borderRadius: 12, padding: '20px 28px', background: c.error + '22', border: `1px solid ${c.error}44`, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <div style={{ fontSize: 32, fontWeight: 800, color: c.error }}>{again}</div>
            <div style={{ fontSize: 13, color: c.secondary }}>Again</div>
          </div>
          <div style={{ borderRadius: 12, padding: '20px 28px', background: c.accent + '22', border: `1px solid ${c.accentBorder}`, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <div style={{ fontSize: 32, fontWeight: 800, color: c.accent }}>{gotIt}</div>
            <div style={{ fontSize: 13, color: c.secondary }}>Got It</div>
          </div>
          <div style={{ borderRadius: 12, padding: '20px 28px', background: c.success + '22', border: `1px solid ${c.success}44`, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <div style={{ fontSize: 32, fontWeight: 800, color: c.success }}>{easy}</div>
            <div style={{ fontSize: 13, color: c.secondary }}>Easy</div>
          </div>
        </div>
        {again > 0 && <p style={{ fontSize: 14, color: c.muted, margin: 0 }}>{again} card{again > 1 ? 's' : ''} will come back tomorrow. Keep it up!</p>}
        <button onClick={onFinish} style={{ marginTop: 8, padding: '12px 28px', background: c.card, border: `1px solid ${c.border}`, borderRadius: 10, color: c.text, cursor: 'pointer', fontSize: 14 }}>← Back to Decks</button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 600, margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24 }}>
      {/* Progress bar */}
      <div style={{ width: '100%', height: 4, background: c.border, borderRadius: 99, overflow: 'hidden' }}>
        <div style={{ height: '100%', background: c.accent, transition: 'width 0.3s ease', borderRadius: 99, width: `${(index / cards.length) * 100}%` }} />
      </div>
      <div style={{ fontSize: 13, color: c.muted }}>{index + 1} / {cards.length}</div>

      {/* Card */}
      <div style={{ width: '100%', cursor: 'pointer' }} onClick={() => !flipped && setFlipped(true)}>
        <div style={{ background: c.card, border: `1px solid ${flipped ? c.accent : c.border}`, borderRadius: 20, padding: '48px 36px', minHeight: 220, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, textAlign: 'center', transition: 'border-color 0.2s' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: flipped ? c.accent : c.muted, letterSpacing: 1.5, textTransform: 'uppercase' }}>{flipped ? 'ANSWER' : 'QUESTION'}</div>
          <div style={{ fontSize: 20, fontWeight: 500, color: c.text, lineHeight: 1.6 }}>{flipped ? card.back : card.front}</div>
          {!flipped && <div style={{ fontSize: 12, color: c.muted, marginTop: 8 }}>tap to reveal answer</div>}
        </div>
      </div>

      {/* Rating buttons */}
      {flipped && (
        <div style={{ display: 'flex', gap: 12, width: '100%' }}>
          <button onClick={() => rate(QUALITY_AGAIN)} style={{ flex: 1, padding: '14px 8px', borderRadius: 12, border: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, background: c.error + '22', color: c.error }}>
            <span style={{ fontSize: 20 }}>↩</span>
            <span style={{ fontSize: 14, fontWeight: 700 }}>Again</span>
            <span style={{ fontSize: 11, opacity: 0.7 }}>didn't know</span>
          </button>
          <button onClick={() => rate(QUALITY_GOT_IT)} style={{ flex: 1, padding: '14px 8px', borderRadius: 12, border: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, background: c.accent + '22', color: c.accent }}>
            <span style={{ fontSize: 20 }}>✓</span>
            <span style={{ fontSize: 14, fontWeight: 700 }}>Got It</span>
            <span style={{ fontSize: 11, opacity: 0.7 }}>review tomorrow</span>
          </button>
          <button onClick={() => rate(QUALITY_EASY)} style={{ flex: 1, padding: '14px 8px', borderRadius: 12, border: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, background: c.success + '22', color: c.success }}>
            <span style={{ fontSize: 20 }}>⚡</span>
            <span style={{ fontSize: 14, fontWeight: 700 }}>Easy</span>
            <span style={{ fontSize: 11, opacity: 0.7 }}>knew it cold</span>
          </button>
        </div>
      )}

      <button onClick={onFinish} style={{ background: 'none', border: 'none', color: c.muted, cursor: 'pointer', fontSize: 13, marginTop: 8 }}>✕ Exit</button>
    </div>
  );
}

// ── Main Flashcards Page ─────────────────────────────────────────────────────
export default function Flashcards() {
  const { user }          = useAuth();
  const { tier }          = useUserTier();
  const { openAuthModal } = useAuthModal();
  const navigate          = useNavigate();
  const { theme }         = useTheme();
  const c                 = TOKENS[theme];

  const [decks, setDecks]               = useState([]);
  const [decksLoading, setDecksLoading] = useState(true);
  const [activeDeck, setActiveDeck]     = useState(null);
  const [cards, setCards]               = useState([]);
  const [cardsLoading, setCardsLoading] = useState(false);

  const isPaid = tier === 'pro' || tier === 'team' || tier === 'admin';

  useEffect(() => {
    async function load() {
      setDecksLoading(true);
      const url = user ? `${API}/flashcard-decks?userId=${user.id}` : `${API}/flashcard-decks`;
      const res  = await fetch(url);
      const data = await res.json();
      setDecks(data);
      setDecksLoading(false);
    }
    load();
  }, [user]);

  async function selectDeck(deck) {
    if (!deck.is_free && !isPaid) { navigate('/pricing'); return; }
    if (!deck.is_free && !user)   { openAuthModal(); return; }
    setCardsLoading(true);
    setActiveDeck(deck);
    const url = user ? `${API}/flashcard-decks/${deck.id}/cards?userId=${user.id}` : `${API}/flashcard-decks/${deck.id}/cards`;
    const res  = await fetch(url);
    const data = await res.json();
    const today = new Date().toISOString().split('T')[0];
    const sorted = [...data].sort((a, b) => {
      const aDue = !a.progress || a.progress.next_review_date <= today;
      const bDue = !b.progress || b.progress.next_review_date <= today;
      if (aDue && !bDue) return -1;
      if (!aDue && bDue) return 1;
      return 0;
    });
    setCards(sorted);
    setCardsLoading(false);
  }

  const rateCard = useCallback(async (cardId, quality) => {
    if (!user) return;
    await fetch(`${API}/flashcard-progress`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId: user.id, cardId, quality }),
    });
  }, [user]);

  return (
    <>
      <SEO title="Flashcards — TrailCode" description="Master Salesforce concepts with spaced repetition flashcards." />
      <div style={{ minHeight: '100vh', background: c.bg, color: c.text, fontFamily: "'Inter', sans-serif", transition: 'background 0.25s, color 0.25s' }}>
        <Navbar />

        <div style={{ padding: '48px 24px' }}>
          {/* Header */}
          {!activeDeck && (
            <div style={{ textAlign: 'center', marginBottom: 40 }}>
              <h1 style={{ fontSize: 36, fontWeight: 700, color: c.text, margin: '0 0 8px' }}>Flashcards</h1>
              <p style={{ fontSize: 16, color: c.secondary, margin: 0 }}>
                Spaced repetition — cards you struggle with come back sooner.
                {!user && <span> <button onClick={openAuthModal} style={{ background: 'none', border: 'none', color: c.accent, cursor: 'pointer', fontSize: 'inherit', padding: 0, textDecoration: 'underline' }}>Sign in</button> to save your progress.</span>}
              </p>
            </div>
          )}

          {/* Session or deck list */}
          {activeDeck ? (
            cardsLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                <div style={{ width: 40, height: 40, border: `3px solid ${c.border}`, borderTop: `3px solid ${c.accent}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
              </div>
            ) : (
              <StudySession deck={activeDeck} cards={cards} userId={user?.id} onRate={rateCard} c={c} onFinish={() => { setActiveDeck(null); setCards([]); }} />
            )
          ) : (
            <>
              <DeckList decks={decks} onSelect={selectDeck} loading={decksLoading} c={c} />
              {!isPaid && (
                <div style={{ textAlign: 'center', marginTop: 32, color: c.secondary, fontSize: 14 }}>
                  🔒 Pro unlocks all decks — <button onClick={() => navigate('/pricing')} style={{ background: 'none', border: 'none', color: c.accent, cursor: 'pointer', fontSize: 'inherit', padding: 0, textDecoration: 'underline' }}>Upgrade for $9/mo</button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
}